import { z } from 'zod';
import { BAR_STYLES, CANDLE_FINISHES, CANDLE_STYLES, CENTERPIECE_STYLES, CHAIR_STYLES, CHUPPAH_STYLES, COCKTAIL_TABLE_STYLES, CUTLERY_FINISHES, DANCE_FLOOR_STYLES, DECOR_FLOOR_FINISHES, GLASS_STYLES, GLASS_SHAPES, LINEN_FINISHES, NAPKIN_FOLDS, LOUNGE_STYLES, MAX_FLOWER_COLORS, } from '../types/decor.js';
import { DECOR_LIBRARY_CATEGORIES, MAX_DECOR_LIBRARY_READ_PICTURES, MAX_DECOR_LIBRARY_TAGS, MAX_DECOR_LIBRARY_VENDORS, } from '../types/decor-library.js';
import { MAX_PALETTE_COLORS } from '../utils/decor.js';
/** A colour as `#rrggbb`. */
export const hexColorSchema = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'A colour is written #rrggbb')
    .transform((c) => c.toLowerCase());
/** Every field of the look, each optional — no picks. */
const lookFields = {
    linenColor: hexColorSchema,
    napkinColor: hexColorSchema,
    chairStyle: z.enum(CHAIR_STYLES),
    chairColor: hexColorSchema,
    cushionColor: hexColorSchema,
    centerpiece: z.enum(CENTERPIECE_STYLES),
    flowerColors: z.array(hexColorSchema).min(1).max(MAX_FLOWER_COLORS),
    greeneryColor: hexColorSchema,
    charger: z.boolean(),
    chargerColor: hexColorSchema,
    plateColor: hexColorSchema,
    cutlery: z.enum(CUTLERY_FINISHES),
    glass: z.enum(GLASS_STYLES),
    glassColor: hexColorSchema,
    wallColor: hexColorSchema,
    floorColor: hexColorSchema,
    floorFinish: z.enum(DECOR_FLOOR_FINISHES),
    linenFinish: z.enum(LINEN_FINISHES),
    napkinFold: z.enum(NAPKIN_FOLDS),
    glassShape: z.enum(GLASS_SHAPES),
    candleStyle: z.enum(CANDLE_STYLES),
    candleFinish: z.enum(CANDLE_FINISHES),
    cocktailStyle: z.enum(COCKTAIL_TABLE_STYLES),
    cocktailColor: hexColorSchema,
    barStyle: z.enum(BAR_STYLES),
    barColor: hexColorSchema,
    danceFloorStyle: z.enum(DANCE_FLOOR_STYLES),
    chuppahStyle: z.enum(CHUPPAH_STYLES),
    chuppahFabricColor: hexColorSchema,
    chuppahFlowerColors: z.array(hexColorSchema).min(1).max(MAX_FLOWER_COLORS),
    loungeStyle: z.enum(LOUNGE_STYLES),
    loungeColor: hexColorSchema,
};
/** A look without picks — what a library piece and a pick carry. */
export const decorLookFieldsSchema = z.object(lookFields).partial().strict();
export const decorPickSchema = z
    .object({
    itemId: z.string().uuid(),
    name: z.string().max(200),
    look: decorLookFieldsSchema,
})
    .strict();
export const decorLookSchema = z
    .object({
    ...lookFields,
    picks: z.record(z.enum(DECOR_LIBRARY_CATEGORIES), decorPickSchema),
})
    .partial()
    .strict();
export const decorPaletteSchema = z
    .object({
    chairs: z.array(hexColorSchema).max(MAX_PALETTE_COLORS),
    flowers: z.array(hexColorSchema).max(MAX_PALETTE_COLORS),
    tableware: z.array(hexColorSchema).max(MAX_PALETTE_COLORS),
    hallWall: hexColorSchema,
    hallFloor: hexColorSchema,
})
    .partial()
    .strict();
/** `PUT /events/:id/decor` — the event's whole look and its pictures' colours. */
export const eventDecorSchema = z
    .object({ look: decorLookSchema, palette: decorPaletteSchema })
    .strict();
/** `PUT /floor-plans/:id/decor` — the plan's override; null takes it away. */
export const floorPlanDecorSchema = z.object({ look: decorLookSchema.nullable() }).strict();
// ─── The decor library ──────────────────────────────────────────────────────
const libraryVendorSchema = z
    .object({
    vendorId: z.string().uuid(),
    notes: z.string().trim().max(500).nullish(),
})
    .strict();
const libraryItemFields = {
    category: z.enum(DECOR_LIBRARY_CATEGORIES),
    name: z.string().trim().min(1, 'Give the piece a name').max(200),
    description: z.string().trim().max(2000).nullish(),
    tags: z.array(z.string().trim().min(1).max(40)).max(MAX_DECOR_LIBRARY_TAGS),
    look: decorLookFieldsSchema,
    colors: z.array(hexColorSchema).max(MAX_PALETTE_COLORS),
    isActive: z.boolean(),
    vendors: z.array(libraryVendorSchema).max(MAX_DECOR_LIBRARY_VENDORS),
    /** Picture ids in the order to show them; the first is the cover. */
    pictureOrder: z.array(z.string().uuid()).max(50),
};
/** `POST /decor-library` */
export const createDecorLibraryItemSchema = z
    .object({
    category: libraryItemFields.category,
    name: libraryItemFields.name,
})
    .extend(z
    .object({
    description: libraryItemFields.description,
    tags: libraryItemFields.tags,
    look: libraryItemFields.look,
    colors: libraryItemFields.colors,
    isActive: libraryItemFields.isActive,
    vendors: libraryItemFields.vendors,
})
    .partial().shape)
    .strict();
/** `PATCH /decor-library/:id` */
export const updateDecorLibraryItemSchema = z.object(libraryItemFields).partial().strict();
/** `POST /decor-library/read` — the AI reads pictures already uploaded. */
export const readDecorLibrarySchema = z
    .object({
    /** A piece's own pictures… */
    itemId: z.string().uuid().optional(),
    /** …or pictures just uploaded, not yet on a piece. */
    s3Keys: z.array(z.string().min(1).max(500)).max(MAX_DECOR_LIBRARY_READ_PICTURES).optional(),
    /**
     * …or the pictures as the form shows them, in its order: the piece's own
     * by id (`itemId` required) and new uploads by key — so an edited form is
     * read as it stands, not as it was saved.
     */
    pictures: z
        .array(z.union([
        z.object({ id: z.string().uuid() }).strict(),
        z.object({ s3Key: z.string().min(1).max(500) }).strict(),
    ]))
        .min(1)
        .max(MAX_DECOR_LIBRARY_READ_PICTURES)
        .optional(),
    category: z.enum(DECOR_LIBRARY_CATEGORIES).optional(),
})
    .strict()
    .refine((v) => !!v.itemId || (v.s3Keys?.length ?? 0) > 0 || !!v.pictures, {
    message: 'Name the piece or the pictures to read',
})
    .refine((v) => !!v.itemId || !(v.pictures ?? []).some((p) => 'id' in p), {
    message: "A piece's own pictures need the piece",
    path: ['itemId'],
});
//# sourceMappingURL=decor.js.map